import { environmentToChipKey } from '../../AppSwitcher/useAppSwitcherTree';
import {
  AppSwitcherData,
  Entity,
  Tenant,
} from '../../hooks/useAppSwitcherDataTypes';
import { AppsV1IdentityResponse, TenantInfo } from '../../types';
import {
  EnvironmentTier,
  getEnvironmentTier,
} from '../../utils/getEnvironmentTier';
import { Variant } from '../hooks/useVariant';

export class AppSwitcherAdapter {
  private static getCurrentTenantId(
    appType: string,
    billingIdentityData?: AppsV1IdentityResponse,
  ): string | null {
    try {
      if (appType === 'BILLING') {
        // For billing, get from identity API
        if (billingIdentityData) return billingIdentityData.tenantId;
      } else if (appType === 'REVENUE') {
        // For revenue, get from localStorage
        return localStorage.getItem('ZRevenue_TenantID');
      }
    } catch (error) {
      console.warn(`Failed to get current tenant ID for ${appType}:`, error);
    }

    return null;
  }

  /**
   * Transforms AppSwitcher data to the format expected by TenantDataExtractor
   */
  static transformAppSwitcherData(
    appSwitcherData: AppSwitcherData,
    identityData?: AppsV1IdentityResponse,
    variant?: Variant,
  ): {
    billingEntities: TenantInfo[];
    revenueEntities: TenantInfo[];
    allEntities: TenantInfo[];
  } {
    if (!appSwitcherData?.data?.tenants) {
      console.warn('No tenants found in AppSwitcher data');
      return {
        billingEntities: [],
        revenueEntities: [],
        allEntities: [],
      };
    }

    // First, build the tenant hierarchy based on linkedTenantId (keep all app types)
    const tenantsWithChildren = this.buildTenantHierarchy(
      appSwitcherData.data.tenants,
    );

    const billingEntities: TenantInfo[] = [];
    const revenueEntities: TenantInfo[] = [];
    const allEntities: TenantInfo[] = [];

    // Collect all tenants of each type (including children that should be promoted to root)
    const allBillingTenants = this.collectTenantsByType(
      tenantsWithChildren,
      'BILLING',
    );
    const allRevenueTenants = this.collectTenantsByType(
      tenantsWithChildren,
      'REVENUE',
    );
    const allTenants = this.collectTenants(tenantsWithChildren);

    // Transform billing entities (filter children to billing only)
    allBillingTenants.forEach((tenant) => {
      const transformedTenant = this.transformTenant(
        tenant,
        'BILLING',
        identityData,
      );
      billingEntities.push(transformedTenant);
    });

    // Transform revenue entities (filter children to revenue only)
    allRevenueTenants.forEach((tenant) => {
      const transformedTenant = this.transformTenant(
        tenant,
        'REVENUE',
        identityData,
      );
      revenueEntities.push(transformedTenant);
    });

    allTenants.forEach((tenant) => {
      allEntities.push(
        this.transformTenant(
          tenant,
          variant === Variant.BILLING ? 'BILLING' : 'REVENUE',
          identityData,
        ),
      );
    });

    return {
      billingEntities,
      revenueEntities,
      allEntities,
    };
  }

  /**
   * Builds the tenant hierarchy based on linkedTenantId relationships
   */
  private static buildTenantHierarchy(tenants: Tenant[]): Tenant[] {
    const tenantMap = new Map<string, Tenant>();

    // First pass: create enhanced tenant objects and map them
    const enhancedTenants = tenants.map((tenant) => ({
      ...tenant,
      children: [] as Tenant[],
      isChild: false,
    }));

    enhancedTenants.forEach((tenant) => {
      if (tenant.id) {
        tenantMap.set(tenant.id, tenant);
      }
    });

    // Second pass: build parent-child relationships
    enhancedTenants.forEach((tenant) => {
      if (tenant.linkedBillingZuoraTenantId) {
        const parentTenant = tenantMap.get(tenant.linkedBillingZuoraTenantId);
        if (parentTenant) {
          if (!parentTenant.children) {
            parentTenant.children = [];
          }
          parentTenant.children.push(tenant);
          tenant.isChild = true;
        } else {
          console.warn(
            `Parent tenant with ID ${tenant.linkedBillingZuoraTenantId} not found for tenant ${tenant.id}`,
          );
        }
      } else {
        const isBillingBilling =
          tenant.entities?.[0]?.rootTenantId &&
          tenant.entities[0].rootTenantId !== tenant.id &&
          tenantMap.get(tenant.entities[0].rootTenantId);
        if (isBillingBilling) {
          const parentTenant = tenantMap.get(
            tenant.entities?.[0]?.rootTenantId || '',
          );
          if (parentTenant) {
            if (!parentTenant.children) {
              parentTenant.children = [];
            }
            parentTenant.children.push(tenant);
            tenant.isChild = true;
          }
        }
      }
    });

    return enhancedTenants;
  }

  static transformFromIdentityData(identityData: AppsV1IdentityResponse): {
    entities: TenantInfo[];
  } {
    if (!identityData) {
      return { entities: [] };
    }

    // Create a TenantInfo from identity data
    const currentTenant = this.createTenantFromIdentity(identityData);

    return {
      entities: [currentTenant],
    };
  }

  /**
   * Creates a single TenantInfo from identity data
   */
  private static createTenantFromIdentity(
    identityData: AppsV1IdentityResponse,
  ): TenantInfo {
    const environmentTierToString = (tier: EnvironmentTier): string => {
      switch (tier) {
        case EnvironmentTier.DEV:
          return 'dev';
        case EnvironmentTier.STG:
          return 'stg';
        case EnvironmentTier.PREPROD:
          return 'preprod';
        case EnvironmentTier.SBX:
          return 'sbx';
        case EnvironmentTier.PROD:
          return 'prod';
        default:
          return 'prod';
      }
    };
    // Extract tenant info from identity data structure
    const tenantId = identityData.tenantId;
    const tenantName = identityData.tenantName;
    const environmentTier = getEnvironmentTier();
    const environment = environmentTierToString(environmentTier);

    const transformedTenant: TenantInfo = {
      tenantId,
      name: tenantName,
      environment: environment,
      isDefault: false,
      isCurrent: true, // This is the current tenant since we're already on it
      href: '',
      children: [], // No children available without appSwitcherData
    };

    return transformedTenant;
  }

  /**
   * Transforms a single tenant from AppSwitcher format to TenantInfo
   */
  private static transformTenant(
    tenant: Tenant,
    filterAppType?: string,
    billingIdentityData?: AppsV1IdentityResponse,
  ): TenantInfo {
    const primaryEntity = this.findPrimaryEntity(tenant.entities || []);

    const isDefault = false;

    const currentTenantId = this.getCurrentTenantId(
      filterAppType || '',
      billingIdentityData,
    );
    const isCurrent = tenant.id === currentTenantId;
    const href = this.getHref(tenant, primaryEntity);

    const transformedTenant: TenantInfo = {
      tenantId: tenant.id || '',
      name: tenant.name || '',
      environment:
        environmentToChipKey(
          tenant.environment ?? 'prod',
          tenant.tenantCategory,
        ) || 'prod',
      isDefault,
      isCurrent,
      href,
      children: this.transformChildren(
        tenant.children,
        filterAppType,
        billingIdentityData,
      ),
    };

    return transformedTenant;
  }

  private static collectTenantsByType(
    tenants: Tenant[],
    appType: string,
  ): Tenant[] {
    const result: Tenant[] = [];
    const processed = new Set<string>();

    const rootTenants = tenants.filter(
      (t) => !t.isChild && t.appType === appType,
    );
    rootTenants.forEach((tenant) => {
      result.push(tenant);
      processed.add(tenant.id || '');
    });

    const findCrossTypeChildren = (tenant: Tenant, parentAppType: string) => {
      if (tenant.children && tenant.children.length > 0) {
        tenant.children.forEach((child) => {
          if (
            child.appType === appType &&
            child.appType !== parentAppType &&
            !processed.has(child.id || '')
          ) {
            const promotedTenant = {
              ...child,
              linkedTenantId: undefined,
              children: child.children,
            };
            result.push(promotedTenant);
            processed.add(child.id || '');
          } else {
            findCrossTypeChildren(child, child.appType || parentAppType);
          }
        });
      }
    };

    const allRootTenants = tenants.filter((t) => !t.isChild);
    allRootTenants.forEach((rootTenant) => {
      findCrossTypeChildren(rootTenant, rootTenant.appType || '');
    });

    return result;
  }

  private static collectTenants(tenants: Tenant[]): Tenant[] {
    return tenants.filter((t) => !t.isChild);
  }

  /**
   * Finds the primary entity for a tenant
   */
  private static findPrimaryEntity(entities: Entity[]): Entity | null {
    if (!entities || entities.length === 0) {
      console.warn('No entities found for tenant');
      return null;
    }

    const ownerEntity = entities.find((entity) => entity.owner);
    if (ownerEntity) {
      return ownerEntity;
    }

    const accessibleEntity = entities.find((entity) => entity.accessible);
    if (accessibleEntity) {
      return accessibleEntity;
    }

    return entities[0];
  }

  /**
   * Determines if a tenant should be considered default
   */
  private static determineIsDefault(): boolean {
    // Logic TBD
    return false;
  }

  /**
   * Gets the href for the tenant
   */
  private static getHref(tenant: Tenant, primaryEntity: Entity | null): string {
    // Use entity's targetUrl if available
    if (primaryEntity?.targetUrl) {
      return primaryEntity.targetUrl;
    }

    // Fallback to constructing URL based on tenant info
    const baseUrl = window.location.origin;
    return `${baseUrl}/app/${tenant.appType?.toLowerCase()}/${tenant.id}`;
  }

  /**
   * Transforms child tenants recursively
   */
  //   private static transformChildren(
  //     children?: Tenant[],
  //     parentAppType?: string,
  //   ): TenantInfo[] | undefined {
  //     if (!children || children.length === 0) {
  //       return undefined;
  //     }

  //     const filteredChildren = children
  //       .filter(
  //         (child) =>
  //           child.validTenant &&
  //           child.id &&
  //           child.name &&
  //           child.appType === parentAppType,
  //       )
  //       .map((child) => this.transformTenant(child));

  //     // Return undefined if no children remain after filtering
  //     return filteredChildren.length > 0 ? filteredChildren : undefined;
  //   }
  // }

  private static transformChildren(
    children: Tenant[] | undefined,
    filterAppType?: string,
    billingIdentityData?: AppsV1IdentityResponse,
  ): TenantInfo[] | undefined {
    if (!children || children.length === 0) {
      return undefined;
    }

    const transformedChildren: TenantInfo[] = [];

    children.forEach((child) => {
      const transformedChild = this.transformTenant(
        child,
        filterAppType,
        billingIdentityData,
      );
      transformedChildren.push(transformedChild);
    });

    return transformedChildren.length > 0 ? transformedChildren : undefined;
  }
}
